import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  CircularProgress,
} from '@mui/material'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import FolderIcon from '@mui/icons-material/Folder'
import PlaceIcon from '@mui/icons-material/Place'
import { getJson, getUrl } from '../../providers/data'

interface TreeItem {
  id: string
  code: string
  naam: string
  gwb: number | null
  profiel_code: string | null
  parent_id: string | null
  selected: boolean
  has_children: boolean
}

interface GebiedTreeViewProps {
  gemeenteSid: number
  selectedGebiedCode: string
  onSelectGebied: (code: string) => void
}

const GebiedTreeView: React.FC<GebiedTreeViewProps> = ({
  gemeenteSid,
  selectedGebiedCode,
  onSelectGebied,
}) => {
  const [items, setItems] = useState<TreeItem[]>([])
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const fetchTree = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getJson<TreeItem[]>(
        `${getUrl()}/gemeenten/${gemeenteSid}/gebiedsindeling/tree`
      )
      setItems(result)
      // Auto-expand root
      if (result.length > 0) {
        const roots = result.filter(i => !i.parent_id)
        setExpanded(new Set(roots.map(r => r.id)))
      }
    } catch (err) {
      console.error('Failed to load tree:', err)
    } finally {
      setLoading(false)
    }
  }, [gemeenteSid])

  useEffect(() => {
    if (gemeenteSid) {
      void fetchTree()
    }
  }, [gemeenteSid, fetchTree])

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const getChildren = (parentId: string | null): TreeItem[] => items.filter(i => i.parent_id === parentId)

  const renderItem = (item: TreeItem, depth = 0) => {
    const children = getChildren(item.id)
    const hasChildren = children.length > 0
    const isExpanded = expanded.has(item.id)
    const isSelected = item.code === selectedGebiedCode

    return (
      <React.Fragment key={item.id}>
        <ListItemButton
          sx={{
            pl: 2 + depth * 2,
            backgroundColor: isSelected ? 'action.selected' : undefined,
          }}
          onClick={() => {
            onSelectGebied(item.code)
            if (hasChildren) {
              toggleExpand(item.id)
            }
          }}
          dense
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            {hasChildren ? <FolderIcon fontSize="small" /> : <PlaceIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" sx={{ fontWeight: isSelected ? 'bold' : undefined }}>
                {item.naam || item.code}
              </Typography>
            }
            secondary={item.code !== item.naam ? item.code : undefined}
          />
          {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map(child => renderItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    )
  }

  if (loading) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    )
  }

  const roots = items.filter(i => !i.parent_id)

  if (roots.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Geen gebiedsindeling beschikbaar.
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ maxHeight: 500, overflow: 'auto' }}>
      <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2">Gebiedsindeling</Typography>
      </Box>
      <List dense disablePadding>
        {roots.map(root => renderItem(root))}
      </List>
    </Paper>
  )
}

export default GebiedTreeView
